"use client";

import { SELFIE_ROOM_BOOKING_LEAD_SESSION_METADATA_KEY } from "@calcom/features/bookings/lib/selfieroomBookingLeads";
import type { BookerEvent } from "@calcom/features/bookings/types";
import { useCallback, useMemo, useRef } from "react";

type BookingLeadOptions = {
  eventType:
    | Pick<BookerEvent, "metadata" | "locations" | "bookingFields"> & {
        id?: number | null;
        slug?: string | null;
      }
    | null
    | undefined;
  eventTypeId?: number | null;
  eventTypeSlug?: string | null;
  username?: string | null;
  timeslot?: string | null;
  timeZone?: string | null;
  disabled?: boolean;
};

type BookingLeadFormValues = {
  responses: unknown;
} & Record<string, unknown>;

const CONSENT_TEXT =
  "Selfie Room may use your contact details to help complete this booking and send booking-related reminders.";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === "object" && !Array.isArray(value));

const getResponseString = (responses: unknown, key: string) => {
  if (!isRecord(responses)) return "";
  const value = responses[key];
  return typeof value === "string" ? value.trim() : "";
};

const getName = (responses: unknown) => {
  if (!isRecord(responses)) return "";
  const name = responses.name;
  if (typeof name === "string") return name.trim();
  if (!isRecord(name)) return "";
  return [name.firstName, name.lastName].filter((value) => typeof value === "string" && value.trim()).join(" ");
};

const cleanEmail = (value: string) => {
  const email = value.trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : "";
};

const cleanPhone = (value: string) => {
  const phone = value.trim();
  return phone.replace(/\D/g, "").length >= 7 ? phone : "";
};

const getSessionId = (eventTypeId?: number | null, eventTypeSlug?: string | null) => {
  const storageKey = `${SELFIE_ROOM_BOOKING_LEAD_SESSION_METADATA_KEY}:${
    eventTypeId || eventTypeSlug || "unknown"
  }`;

  try {
    const existing = window.sessionStorage.getItem(storageKey);
    if (existing) {
      window.sessionStorage.setItem(SELFIE_ROOM_BOOKING_LEAD_SESSION_METADATA_KEY, existing);
      return existing;
    }

    const generated =
      typeof window.crypto?.randomUUID === "function"
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.sessionStorage.setItem(storageKey, generated);
    window.sessionStorage.setItem(SELFIE_ROOM_BOOKING_LEAD_SESSION_METADATA_KEY, generated);
    return generated;
  } catch {
    return "";
  }
};

const isSelfieRoomEvent = ({
  eventType,
  username,
}: {
  eventType: BookingLeadOptions["eventType"];
  username?: string | null;
}) => {
  if (username === "selfieroom" || username === "selfieroom-ca-qk1ven") return true;
  if (!isRecord(eventType?.metadata)) return false;
  const metadata: Record<string, unknown> = eventType.metadata;
  const selfieroom = metadata.selfieroom;
  return isRecord(selfieroom) && selfieroom.service === true;
};

const getSelfieRoomMetadataValue = (eventType: BookingLeadOptions["eventType"], key: string) => {
  if (!isRecord(eventType?.metadata)) return "";
  const metadata: Record<string, unknown> = eventType.metadata;
  const selfieroom = metadata.selfieroom;
  if (!isRecord(selfieroom)) return "";
  const value = selfieroom[key];
  return typeof value === "string" ? value : "";
};

const getLastStep = ({
  email,
  phone,
  locationSlug,
  timeslot,
}: {
  email: string;
  phone: string;
  locationSlug: string;
  timeslot?: string | null;
}) => {
  if (timeslot && locationSlug && email && phone) return "ready_to_submit";
  if (email || phone) return "contact_entered";
  if (timeslot) return "time_selected";
  if (locationSlug) return "location_selected";
  return "form_started";
};

export const useSelfieRoomBookingLead = ({
  eventType,
  eventTypeId,
  eventTypeSlug,
  username,
  timeslot,
  timeZone,
  disabled,
}: BookingLeadOptions) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPayloadRef = useRef("");

  const shouldTrack = useMemo(
    () => !disabled && isSelfieRoomEvent({ eventType, username }),
    [disabled, eventType, username]
  );

  return useCallback(
    (values: BookingLeadFormValues) => {
      if (!shouldTrack || typeof window === "undefined") return;
      const responses = values.responses;
      const email = cleanEmail(getResponseString(responses, "email"));
      const phone = cleanPhone(
        getResponseString(responses, "attendeePhoneNumber") ||
          getResponseString(responses, "phone") ||
          getResponseString(responses, "phoneNumber")
      );

      if (!email && !phone) return;

      const resolvedEventTypeId = eventTypeId ?? eventType?.id ?? null;
      const resolvedEventTypeSlug = eventTypeSlug ?? eventType?.slug ?? null;
      const locationSlug =
        getResponseString(responses, "selfieroomLocation") ||
        getSelfieRoomMetadataValue(eventType, "locationSlug");
      const sessionId = getSessionId(resolvedEventTypeId, resolvedEventTypeSlug);
      if (!sessionId) return;

      const payload = {
        sessionId,
        eventTypeId: resolvedEventTypeId,
        eventTypeSlug: resolvedEventTypeSlug,
        serviceSlug: getSelfieRoomMetadataValue(eventType, "serviceSlug"),
        locationSlug,
        selectedTime: timeslot,
        timeZone,
        name: getName(responses),
        email,
        phone,
        sourceUrl: window.location.href,
        referrer: document.referrer || "",
        lastStep: getLastStep({ email, phone, locationSlug, timeslot }),
        consentText: CONSENT_TEXT,
        metadata: {
          peopleCount: getResponseString(responses, "selfieroomPeopleCount"),
          backgroundColor: getResponseString(responses, "selfieroomBackgroundColor"),
        },
      };

      const payloadKey = JSON.stringify(payload);
      if (payloadKey === lastPayloadRef.current) return;
      lastPayloadRef.current = payloadKey;

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        fetch("/api/selfieroom/booking-leads", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payloadKey,
          keepalive: true,
        }).catch(() => undefined);
      }, 900);
    },
    [eventType, eventTypeId, eventTypeSlug, shouldTrack, timeZone, timeslot]
  );
};
